import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { TimerText, currentRemaining } from "../TimerText";
import { animName, resolveAnim, formatMetricValue, METRICAS, METRICAS_TWITCH } from "../../lib/elementStyle";
import { ChatCuration } from "./ChatCuration";
import { QuotaMonitor } from "./QuotaMonitor";
import { Eye, EyeOff, Radio, WifiOff, Zap, Minus, Plus, RotateCcw, Play, Pause, Layers, FolderOpen, ChevronDown, ChevronUp, ChevronRight, Twitch, Sheet, Folder } from "lucide-react";
import { t } from "../../i18n";
import { YouTubeIcon } from "../YouTubeIcon";

function LiveTextRow({ el, onChangeText }) {
  const [val, setVal] = useState(el.conteudo || "");
  const timer = useRef();
  useEffect(() => { setVal(el.conteudo || ""); }, [el.id]);
  const handle = (v) => {
    setVal(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onChangeText(el.id, v), 300);
  };
  return (
    <textarea
      value={val}
      rows={2}
      onChange={(e) => handle(e.target.value)}
      className="w-full min-h-[52px] bg-input border border-border rounded-sm px-2 py-1.5 text-xs outline-none focus:border-primary resize-y"
      placeholder={t("Texto ao vivo")}
      data-testid={`live-text-${el.id}`}
    />
  );
}

function ScoreControls({ el, onUpdate }) {
  const val = parseInt(el.conteudo, 10) || 0;
  const change = (d) => onUpdate(el.id, { conteudo: String(Math.max(0, val + d)) });
  const btn = "h-8 w-9 rounded-sm bg-secondary hover:bg-accent transition-colors flex items-center justify-center text-xs font-bold";
  return (
    <div className="flex items-center gap-2">
      <button onClick={() => change(-1)} className={btn} data-testid={`score-minus-${el.id}`}><Minus className="h-3.5 w-3.5" /></button>
      <span className="font-mono text-lg w-12 text-center tabular-nums" data-testid={`score-value-${el.id}`}>{val}</span>
      <button onClick={() => change(1)} className={btn + " text-primary"} data-testid={`score-plus-${el.id}`}><Plus className="h-3.5 w-3.5" /></button>
      <button onClick={() => onUpdate(el.id, { conteudo: "0" })} className="ml-auto text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid={`score-reset-${el.id}`}>
        <RotateCcw className="h-3 w-3" /> {t("zerar")}
      </button>
    </div>
  );
}

function TimerControls({ el, onUpdate }) {
  const tm = el.timer || { duration: 300, remaining: 300, running: false, startedAt: null };
  const [min, setMin] = useState(Math.floor((tm.duration ?? 300) / 60));
  const [sec, setSec] = useState((tm.duration ?? 300) % 60);
  const [expanded, setExpanded] = useState(false);

  const start = () => onUpdate(el.id, { timer: { ...tm, running: true, startedAt: Date.now(), remaining: tm.remaining ?? tm.duration } });
  const pause = () => onUpdate(el.id, { timer: { ...tm, running: false, startedAt: null, remaining: currentRemaining(tm) } });
  const bump = (delta) => {
    const rem = Math.max(0, currentRemaining(tm) + delta);
    onUpdate(el.id, { timer: { ...tm, remaining: rem, startedAt: tm.running ? Date.now() : null } });
  };
  const apply = () => {
    const dur = Math.max(0, Number(min) * 60 + Number(sec));
    onUpdate(el.id, { timer: { ...tm, duration: dur, remaining: dur, running: false, startedAt: null } });
  };
  const modo = tm.modo || "regressiva";
  const setMode = (m) => onUpdate(el.id, { timer: { ...tm, modo: m, remaining: currentRemaining(tm), startedAt: tm.running ? Date.now() : null } });

  const inp = "w-12 h-8 bg-input border border-border rounded-sm px-1 text-xs text-center outline-none focus:border-primary";
  const chip = "h-8 px-2 rounded-sm bg-secondary hover:bg-accent transition-colors flex items-center justify-center text-xs font-semibold gap-1";
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <TimerText timer={tm} style={{ fontFamily: "JetBrains Mono", fontSize: "22px", fontWeight: 700, color: "#fff", minWidth: 74 }} />
        {tm.running ? (
          <Button onClick={pause} size="sm" variant="secondary" className="rounded-sm h-8" data-testid={`timer-pause-${el.id}`}>
            <Pause className="h-3.5 w-3.5 mr-1" /> {t("Pausar")}
          </Button>
        ) : (
          <Button onClick={start} size="sm" className="rounded-sm h-8" data-testid={`timer-start-${el.id}`}>
            <Play className="h-3.5 w-3.5 mr-1" /> {t("Iniciar")}
          </Button>
        )}
        <button onClick={() => setExpanded((e) => !e)} className={chip + " ml-auto"} title={t("Mais opções")} data-testid={`timer-expand-${el.id}`}>
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>
      {expanded && (<>
      <div className="flex items-center gap-1.5">
        <button onClick={() => setMode("regressiva")} className={`${chip} ${modo === "regressiva" ? "bg-primary text-primary-foreground" : ""}`} data-testid={`timer-mode-regressiva-${el.id}`}>↓ {t("Regressiva")}</button>
        <button onClick={() => setMode("progressiva")} className={`${chip} ${modo === "progressiva" ? "bg-primary text-primary-foreground" : ""}`} data-testid={`timer-mode-progressiva-${el.id}`}>↑ {t("Progressiva")}</button>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <button onClick={() => bump(60)} className={chip + " text-primary"} data-testid={`timer-addmin-${el.id}`}>+min</button>
        <button onClick={() => bump(-60)} className={chip} data-testid={`timer-submin-${el.id}`}>−min</button>
        <button onClick={() => bump(1)} className={chip + " text-primary"} data-testid={`timer-addsec-${el.id}`}>+{t("seg")}</button>
        <button onClick={() => bump(-1)} className={chip} data-testid={`timer-subsec-${el.id}`}>−{t("seg")}</button>
      </div>
      <div className="flex items-center gap-1.5">
        <input type="number" min={0} value={min} onChange={(e) => setMin(e.target.value)} className={inp} data-testid={`timer-min-${el.id}`} />
        <span className="text-xs text-muted-foreground">min</span>
        <input type="number" min={0} max={59} value={sec} onChange={(e) => setSec(e.target.value)} className={inp} data-testid={`timer-sec-${el.id}`} />
        <span className="text-xs text-muted-foreground">{t("seg")}</span>
        <button onClick={apply} className="ml-auto text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid={`timer-def-${el.id}`}>
          {t("definir base")}
        </button>
      </div>
      </>)}
    </div>
  );
}

function DynamicReadout({ el, ytData, twitchData }) {
  const isTwitch = el.platform === "twitch";
  const data = isTwitch ? twitchData : ytData;
  const lbl = ((isTwitch ? METRICAS_TWITCH : METRICAS).find((m) => m.value === el.metrica) || {}).label || t("Dado");
  let liveSeconds = 0;
  if (el.metrica === "duration" && data?.startTime && data?.isLive) {
    liveSeconds = Math.max(0, (Date.now() - new Date(data.startTime).getTime()) / 1000);
  }
  const val = formatMetricValue(el, data, liveSeconds);
  return (
    <div className="flex items-center justify-between text-[11px]" data-testid={`live-dynamic-${el.id}`}>
      <span className="text-muted-foreground flex items-center gap-1.5">
        {isTwitch ? <Twitch className="h-5 w-5 text-white" /> : <YouTubeIcon />} {lbl}
      </span>
      <span className="font-mono text-foreground">{val}</span>
    </div>
  );
}

export function LivePanel({ elements, onToggle, onChangeText, onUpdate, onTrigger, onHideAll, onToggleGroup, pvSel, onPvToggle, connected, sources, ytData, twitchData,
  chatEnabled, chatDemo, chatSource, chatConn, chatMessages, chatPinned, onChatStart, onChatStop, onChatPin, onChatUnpin, onChatReject, onChatAlert }) {
  const ordered = [...elements].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));
  const hasChatElement = ordered.some((el) => ["chat", "comment", "superchat", "dynamic"].includes(el.tipo));
  const groupNames = [];
  ordered.forEach((el) => { if (el.grupo && !groupNames.includes(el.grupo)) groupNames.push(el.grupo); });
  const ungrouped = ordered.filter((el) => !el.grupo);
  // Textos editáveis ao vivo: o botão ">" abre/recolhe a caixa de "Conteúdo" (lembrado neste navegador).
  const [openText, setOpenText] = useState(() => { try { return JSON.parse(localStorage.getItem("lgfx_live_text_open") || "{}"); } catch { return {}; } });
  // Grupos: textos editáveis começam recolhidos; o clique no nome/pasta abre ou fecha.
  const [openGroups, setOpenGroups] = useState(() => { try { return JSON.parse(localStorage.getItem("lgfx_live_groups_open") || "{}"); } catch { return {}; } });
  const toggleGroupOpen = (g) => setOpenGroups((o) => {
    const n = { ...o, [g]: !o[g] };
    try { localStorage.setItem("lgfx_live_groups_open", JSON.stringify(n)); } catch {}
    return n;
  });
  const toggleText = (id) => setOpenText((o) => {
    const n = { ...o, [id]: !o[id] };
    try { localStorage.setItem("lgfx_live_text_open", JSON.stringify(n)); } catch {}
    return n;
  });

  const renderRow = (el) => {
    const isText = el.tipo === "texto" && !el.papel;
    const fromSheet = isText && !!el.sheetBind?.enabled;
    const editable = isText && !fromSheet;
    const open = editable && !!openText[el.id];
    return (
    <div className="px-3 py-2.5 border-b border-border/40" data-testid={`live-row-${el.id}`}>
      <div className={`flex items-center justify-between gap-2 ${open || el.papel || el.tipo === "dynamic" || fromSheet ? "mb-1.5" : ""}`}>
        <span className="text-xs truncate flex items-center gap-1.5 min-w-0">
          {editable && (
            <button onClick={() => toggleText(el.id)} aria-expanded={open}
              className="-ml-1 p-0.5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary flex-shrink-0"
              title={open ? t("Recolher texto") : t("Alterar texto")} data-testid={`live-text-toggle-${el.id}`}>
              <ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
            </button>
          )}
          {el.visivel === false ? <EyeOff className="h-3 w-3 text-muted-foreground flex-shrink-0" /> : <Eye className="h-3 w-3 text-emerald-400 flex-shrink-0" />}
          <span className="truncate">{el.nome}</span>
        </span>
        <div className="flex items-center gap-2">
          {animName(resolveAnim(el.animacao).entrada, "in") && (
            <button onClick={() => onTrigger(el.id)} className="text-primary hover:text-primary/80" title={t("Disparar animação")} data-testid={`live-trigger-${el.id}`}>
              <Zap className="h-3.5 w-3.5" />
            </button>
          )}
          {onPvToggle && (
            <Switch checked={pvSel.includes(el.id)} onCheckedChange={(v) => onPvToggle(el.id, v)} title={t("Selecionar para o PREVIEW")}
              className="data-[state=checked]:bg-emerald-500" data-testid={`preview-toggle-${el.id}`} />
          )}
          <Switch checked={el.visivel !== false} onCheckedChange={(v) => onToggle(el.id, v)} data-testid={`live-toggle-${el.id}`} />
        </div>
      </div>
      {el.papel === "placar" && <ScoreControls el={el} onUpdate={onUpdate} />}
      {el.papel === "cronometro" && <TimerControls el={el} onUpdate={onUpdate} />}
      {open && <LiveTextRow el={el} onChangeText={onChangeText} />}
      {fromSheet && (
        <p className="text-[10px] text-muted-foreground flex items-center gap-1" data-testid={`live-text-sheet-${el.id}`}>
          <Sheet className="h-3 w-3 text-emerald-400" /> {t("Texto vinculado ao Google Sheets")}
        </p>
      )}
      {el.tipo === "dynamic" && <DynamicReadout el={el} ytData={ytData} twitchData={twitchData} />}
    </div>
    );
  };

  // Texto dentro de grupo: só a edição do conteúdo (a visibilidade é controlada pelo grupo).
  const renderGroupText = (el) => {
    const fromSheet = !!el.sheetBind?.enabled;
    const open = !fromSheet && !!openText[el.id];
    return (
      <div className="pl-5 pr-3 py-2 border-b border-border/40" data-testid={`live-group-text-${el.id}`}>
        <div className={`flex items-center gap-1.5 text-xs min-w-0 ${open || fromSheet ? "mb-1.5" : ""}`}>
          {!fromSheet && (
            <button onClick={() => toggleText(el.id)} aria-expanded={open}
              className="-ml-1 p-0.5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-secondary flex-shrink-0"
              title={open ? t("Recolher texto") : t("Alterar texto")} data-testid={`live-text-toggle-${el.id}`}>
              <ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
            </button>
          )}
          <span className="truncate text-muted-foreground">{el.nome}</span>
        </div>
        {open && <LiveTextRow el={el} onChangeText={onChangeText} />}
        {fromSheet && (
          <p className="text-[10px] text-muted-foreground flex items-center gap-1" data-testid={`live-text-sheet-${el.id}`}>
            <Sheet className="h-3 w-3 text-emerald-400" /> {t("Texto vinculado ao Google Sheets")}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full" data-testid="live-panel">
      <div className="px-3 py-3 border-b border-border/60 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs">
            {connected ? (
              <><span className="h-2 w-2 rounded-full bg-emerald-400" /><span className="text-emerald-400">{t("Conectado")}</span></>
            ) : (
              <><WifiOff className="h-3.5 w-3.5 text-destructive" /><span className="text-destructive">{t("Reconectando…")}</span></>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="sources-indicator">
            <Radio className="h-3.5 w-3.5" /> {t(sources === 1 ? "{n} fonte" : "{n} fontes", { n: sources })}
          </div>
        </div>
        <Button onClick={onHideAll} variant="destructive" className="w-full rounded-sm h-9" data-testid="hide-all-btn">
          <EyeOff className="h-4 w-4 mr-2" /> {t("Ocultar tudo (emergência)")}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {!onPvToggle && <QuotaMonitor />}
        {hasChatElement && (
          <ChatCuration
            enabled={chatEnabled} demo={chatDemo} source={chatSource} conn={chatConn} messages={chatMessages} pinned={chatPinned}
            onStart={onChatStart} onStop={onChatStop} onPin={onChatPin} onUnpin={onChatUnpin}
            onReject={onChatReject} onAlert={onChatAlert}
          />
        )}
        {ordered.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">{t("Sem elementos.")}</p>}

        {groupNames.map((g) => {
          const members = ordered.filter((el) => el.grupo === g);
          const allVisible = members.every((m) => m.visivel !== false);
          const texts = members.filter((el) => el.tipo === "texto" && !el.papel && (String(el.conteudo || "").trim() || el.sheetBind?.enabled || openText[el.id]));
          const gOpen = !!openGroups[g];
          return (
            <div key={`grp-${g}`} data-testid={`live-group-${g}`}>
              <div className="flex items-center justify-between px-3 py-2 bg-secondary/50 border-b border-border/60">
                {texts.length ? (
                  <button onClick={() => toggleGroupOpen(g)} aria-expanded={gOpen}
                    className="text-[10px] font-mono tracking-[0.2em] text-foreground/80 hover:text-foreground flex items-center gap-1.5 truncate min-w-0"
                    title={gOpen ? t("Recolher textos do grupo") : t("Mostrar textos do grupo")} data-testid={`live-group-open-${g}`}>
                    {gOpen ? <FolderOpen className="h-3.5 w-3.5 text-primary flex-shrink-0" /> : <Folder className="h-3.5 w-3.5 text-primary flex-shrink-0" />}
                    <span className="truncate">{g}</span>
                  </button>
                ) : (
                  <span className="text-[10px] font-mono tracking-[0.2em] text-foreground/80 flex items-center gap-1.5 truncate">
                    <Folder className="h-3.5 w-3.5 text-primary" /> {g}
                  </span>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground">{members.length}</span>
                  {onPvToggle && (
                    <Switch checked={members.every((m) => pvSel.includes(m.id))} onCheckedChange={(v) => members.forEach((m) => onPvToggle(m.id, v))}
                      title={t("Selecionar grupo para o PREVIEW")} className="data-[state=checked]:bg-emerald-500" data-testid={`group-preview-toggle-${g}`} />
                  )}
                  <Switch checked={allVisible} onCheckedChange={(v) => onToggleGroup(g, v)} data-testid={`group-toggle-${g}`} />
                </div>
              </div>
              {members.filter((el) => el.papel).map((el) => <div key={el.id}>{renderRow(el)}</div>)}
              {gOpen && texts.map((el) => <div key={el.id}>{renderGroupText(el)}</div>)}
            </div>
          );
        })}

        {ungrouped.map((el) => <div key={el.id}>{renderRow(el)}</div>)}
      </div>
    </div>
  );
}
